import { Box, Chip, Stack, Typography } from '@mui/material';
import QuantityWithUnit from './components/QuantityWithUnit';
import { formatBangkokDateTime } from './utils/bangkokTime';

// Read-only view of demand_snapshot + its stock_allocations, exactly as the server returned them.
// IMPLEMENTATION_PLAN.md Phase 2: "FEFO แสดง order ที่ server คืนมา ไม่จัด stock ใหม่บน client;
// raw demand/post-tumble stock/expected yield คนละคอลัมน์" — we never re-sort or re-derive the
// FEFO order client-side, and raw / post-tumble / yield each get their own labeled column.
export default function DemandAllocationView({ scenario }) {
  const demandRows = Array.isArray(scenario?.demand_snapshot) ? scenario.demand_snapshot : [];

  if (!demandRows.length) {
    return <Typography color="text.secondary">No demand snapshot on this scenario.</Typography>;
  }

  return (
    <Stack spacing={3}>
      {demandRows.map((row, index) => (
        <Box key={row.id ?? index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{row.product_id}</Typography>
              <Typography variant="caption" color="text.secondary">PO / line: {(row.process_orders || []).join(', ') || row.line_id || '—'}</Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Field label="Raw demand" value={row.component_demand_kg} unit="kg raw" />
              <Field label="Post-tumble stock" value={row.stock_used_kg} unit="kg post-tumble" />
              <Field label="Rounded planned output" value={row.rounded_planned_kg} unit="kg" />
            </Stack>
          </Stack>

          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>FEFO stock allocations (server order)</Typography>
          {Array.isArray(row.stock_allocations) && row.stock_allocations.length ? (
            <Box sx={{ overflowX: 'auto', mt: 1 }}>
              <Box component="table" sx={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', '& th, & td': { p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }, '& th': { textAlign: 'left', fontWeight: 600 } }}>
                <thead>
                  <tr>{['Order', 'Barrel / lot', 'Qty (kg)', 'Ready at', 'Expiry', 'Note'].map((label) => <th key={label}>{label}</th>)}</tr>
                </thead>
                <tbody>
                  {row.stock_allocations.map((allocation, allocationIndex) => (
                    <tr key={allocation.id ?? allocationIndex}>
                      <td>{allocationIndex + 1}</td>
                      <td>{allocation.core_tumbler_id ?? '—'}</td>
                      <td><QuantityWithUnit value={allocation.quantity_kg} unit="kg" /></td>
                      <td>{allocation.ready_at ? formatBangkokDateTime(allocation.ready_at) : '—'}</td>
                      <td>{allocation.expiry_at ? formatBangkokDateTime(allocation.expiry_at) : '—'}</td>
                      <td>{allocation.excluded_reason ? <Chip size="small" color="warning" label={allocation.excluded_reason} /> : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              No stock allocation returned for this demand line — fresh production only.
            </Typography>
          )}

          {Array.isArray(row.excluded_candidates) && row.excluded_candidates.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {row.excluded_candidates.length} candidate lot(s) excluded by the server — see reasons above.
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}

function Field({ label, value, unit }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <QuantityWithUnit value={value} unit={unit} />
    </Box>
  );
}
